// Shared ERA date-group status metadata + formatting helpers.
//
// Single source of truth for how a dateGroup `status` renders as a badge and
// which Matched/Unmatched class it belongs to. Consumed by EraPatientGroups
// (the ERA detail view) so the labels stay in lock-step with the ERAList
// summary badges and the backend classifier in eraReportingRoutes.js.

export const STATUS_META = {
  allocated: { label: "Matched", bg: "success", icon: "fa-circle-check" },
  unallocated: { label: "Unmatched", bg: "danger", icon: "fa-circle-exclamation" },
  // Already settled = matched to a claim a prior ERA already finalised; the
  // cash landed, so it reads as a Matched sub-state.
  skipped_finalised: {
    label: "Matched - Already Settled",
    bg: "secondary",
    icon: "fa-circle-info",
  },
  // Matched claim/invoice was soft-deleted before the ERA arrived: nothing to
  // apply against, so it reads as an Unmatched sub-state for the practice.
  skipped_deleted: {
    label: "Unmatched - Deleted Invoice / Claim",
    bg: "danger",
    icon: "fa-trash",
  },
  duplicate_in_bundle: {
    label: "Unmatched - Duplicate",
    bg: "warning",
    icon: "fa-clone",
  },
  // Reversals/clawbacks surface as Unmatched (the practice sorts them) but keep
  // the "Reversal" reason so the audit trail shows WHY it's unmatched.
  reversal: {
    label: "Unmatched - Contains Reversal",
    bg: "danger",
    icon: "fa-rotate-left",
  },
  // All dismissals are a manual finance action (no auto-dismiss exists), so
  // the badge says so.
  dismissed: { label: "Dismissed - Manually", bg: "dark", icon: "fa-eye-slash" },
};

export function statusMeta(status) {
  return (
    STATUS_META[status] || { label: status || "—", bg: "light", icon: "fa-question" }
  );
}

// Which top-level bucket a dateGroup status belongs to. Mirrors the backend
// MATCHED_STATUSES / UNMATCHED_STATUSES split and the ERAList classifier.
export function classOfStatus(status) {
  return status === "allocated" || status === "skipped_finalised"
    ? "matched"
    : "unmatched";
}

// dateGroup.date is normalised to "YYYYMMDD" server-side. `new Date("20260506")`
// returns Invalid Date (the constructor only parses ISO strings with
// separators), so insert the hyphens before formatting. Pass-through for
// already-ISO input keeps the helper safe.
export function formatGroupDate(raw) {
  if (!raw) return "—";
  const s = String(raw);
  const iso = /^\d{8}$/.test(s)
    ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
    : s;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB");
}

export function fmtZAR(x) {
  if (x === null || x === undefined || x === "") return "";
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    minimumFractionDigits: 2,
  }).format(Number(x) || 0);
}

// ─── Line-level matching (the ERA matcher) ───────────────────────────────────
//
// The dateGroup helpers above describe what the OVERNIGHT matcher did. The
// helpers below describe the line-level model the manual matcher writes, where
// a claim's status is derived from its line coverage rather than stored
// independently. Both models coexist: an ERA processed before line matching
// existed has dateGroups and no line state, and the resolution order here
// handles that.
//
// This is the single definition of the new states. There are already three
// places that classify matched/unmatched (this file, ERAList's count helper, and
// the backend pipeline), and they only agree because each was carefully written
// to. Adding a fourth definition of "partially matched" by hand would not stay
// in step, so everything reads these.

export const CLAIM_MATCH_META = {
  matched: { label: "Matched", bg: "success", icon: "fa-circle-check" },
  partially_matched: {
    label: "Partially Matched",
    bg: "warning",
    icon: "fa-circle-half-stroke",
  },
  unmatched: { label: "Unmatched", bg: "danger", icon: "fa-circle-exclamation" },
  dismissed: { label: "Dismissed", bg: "dark", icon: "fa-eye-slash" },
};

export function claimMatchMeta(status) {
  return (
    CLAIM_MATCH_META[status] || { label: status || "-", bg: "light", icon: "fa-question" }
  );
}

/** A line's own state. Absent matchState reads as unmatched. */
export function lineMatchStatus(line) {
  return line?.matchState?.status || "unmatched";
}

/**
 * Auto versus manual, for the visual distinction on both matcher panes.
 *
 * Reads the explicit stamp first. The two inference fallbacks exist only for
 * rows written before matchSource existed, and new data should never need them.
 */
export function matchSourceOf(node) {
  if (node?.matchState?.matchSource) return node.matchState.matchSource;
  if (node?.manuallyAllocated === true) return "manual";
  if (node?.matchedClaim?.statusAfter === "Manually Allocated") return "manual";
  return "auto";
}

/**
 * A claim's derived status. Mirrors computeClaimRollup in
 * backend/src/services/eraMatchService.js, which is authoritative: the stored
 * value is preferred and this recomputation is the fallback for a claim no
 * matcher has touched yet.
 *
 * Measured against every PAID line, not just the live ones, so a claim with a
 * dismissed line cannot read as fully Matched: a dismissal is money that was not
 * collected. `cleared` is what answers "is this off my list", separately.
 */
export function claimMatchStatus(eraClaim) {
  if (eraClaim?.matchStatus) return eraClaim.matchStatus;

  const payable = (eraClaim?.lines || []).filter((l) => Number(l?.paidAmount || 0) > 0);
  if (payable.length === 0) {
    // Nothing was paid, so the badge derives from the zero lines instead:
    // the matcher can map them (a R0.00 payment resolving the outstanding as
    // a shortfall) or dismiss them. Untouched zero claims stay Unmatched.
    const zeros = (eraClaim?.lines || []).filter((l) => Number(l?.paidAmount || 0) === 0);
    const zeroLive = zeros.filter((l) => lineMatchStatus(l) !== "dismissed");
    const zeroMatched = zeroLive.filter((l) => lineMatchStatus(l) === "matched");
    if (zeros.length > 0 && zeroMatched.length === zeros.length) return "matched";
    if (zeros.length > 0 && zeroLive.length === 0) return "dismissed";
    if (zeroMatched.length > 0) return "partially_matched";
    return "unmatched";
  }
  const live = payable.filter((l) => lineMatchStatus(l) !== "dismissed");
  const matched = live.filter((l) => lineMatchStatus(l) === "matched");

  if (matched.length === payable.length) return "matched";
  if (live.length === 0) return "dismissed";
  if (matched.length > 0) return "partially_matched";
  return "unmatched";
}

/**
 * How much work an ERA still holds, across all its claims. Drives the To do
 * count on the ERA list.
 *
 * Counts LINES rather than claims, because a line is the unit a user actions.
 */
export function eraTodoCounts(era) {
  let outstanding = 0;
  let matched = 0;
  let dismissed = 0;

  for (const claim of era?.eraClaims || []) {
    // Prefer the stored rollup where the matcher has written one: it is the
    // same arithmetic and saves walking the lines.
    if (typeof claim.outstandingLineCount === "number") {
      outstanding += claim.outstandingLineCount;
      dismissed += Number(claim.dismissedLineCount || 0);
      for (const line of claim.lines || []) {
        if (Number(line?.paidAmount || 0) > 0 && lineMatchStatus(line) === "matched") {
          matched += 1;
        }
      }
      continue;
    }
    for (const line of claim.lines || []) {
      if (!(Number(line?.paidAmount || 0) > 0)) continue;
      const s = lineMatchStatus(line);
      if (s === "matched") matched += 1;
      else if (s === "dismissed") dismissed += 1;
      else outstanding += 1;
    }
  }

  return { outstanding, matched, dismissed, cleared: outstanding === 0 };
}
